<template>
  <div
    v-if="initialized"
    class="flex min-h-screen w-full bg-surface-50"
  >
    <div
      class="flex min-h-184 w-full flex-col justify-between rounded bg-surface-0 px-8 py-8 md:w-5/12 md:px-12 lg:px-20"
    >
      <div class="flex items-center gap-4">
        <img
          alt="Logo"
          class="mx-auto w-2/5"
          :src="picture_logo_url"
        />
      </div>
      <div class="flex flex-none flex-col justify-center gap-12">
        <div class="flex flex-col gap-4">
          <div
            v-if="action"
            class="flex flex-col gap-2"
          >
            <div class="text-2xl leading-tight font-semibold text-surface-900">
              {{ $t(`login.title_${action}`) }}
            </div>
          </div>
        </div>
        <Message
          v-if="message_success"
          class="mb-2"
          severity="success"
        >
          {{ message_success }}
        </Message>
        <Message
          v-if="message_error"
          class="mb-2"
          icon="pi pi-times-circle"
          severity="error"
        >
          {{ message_error }}
        </Message>
        <Message
          v-if="message_info"
          severity="secondary"
        >
          {{ message_info }}
        </Message>
        <form @submit.prevent="submit">
          <div class="flex flex-col gap-6">
            <div
              v-if="action && action !== ACTION_EXPIRED_RESET && action !== ACTION_EXPIRED_SIGNUP"
              class="flex flex-col gap-2"
            >
              <label
                class="font-medium text-surface-900"
                for="email"
              >
                {{ $t('login.emailAddress') }}
              </label>
              <InputText
                id="email"
                v-model="email"
                autocomplete="username"
                autofocus
                class="w-full"
                :disabled="action === ACTION_RESET || action === ACTION_SIGNUP"
                :invalid="is_email_invalid"
                :placeholder="$t('login.emailAddress')"
                type="text"
                @blur="(email = email.trim()) && (email_touched = true)"
              />
              <Message
                v-if="is_email_invalid"
                severity="error"
                size="small"
                variant="simple"
              >
                {{ email ? $t('login.emailAddressInvalid') : $t('login.emailAddressRequired') }}
              </Message>
            </div>
            <div
              v-if="$_.includes([ACTION_LOGIN, ACTION_RESET, ACTION_SIGNUP], action)"
              class="flex flex-col gap-2"
            >
              <label
                class="font-medium text-surface-900"
                for="password2"
              >
                {{ $t('login.password') }}
              </label>
              <Password
                v-model="password"
                class="w-full"
                :feedback="false"
                :fluid="true"
                :inputProps="{ autocomplete: action === ACTION_LOGIN ? 'current-password' : 'new-password' }"
                :invalid="is_password_invalid"
                :placeholder="$t('login.password')"
                required
                toggleMask
                @blur="password_touched = true"
              />
              <Message
                v-if="is_password_invalid"
                severity="error"
                size="small"
                variant="simple"
              >
                {{ $t('login.passwordRequired') }}
              </Message>
              <Message
                v-if="is_password_invalid_length"
                severity="error"
                size="small"
                variant="simple"
              >
                {{ $t('login.passwordLengthRequired') }}
              </Message>
            </div>
            <div
              v-if="$_.includes([ACTION_SIGNUP, ACTION_RESET], action)"
              class="flex flex-col gap-2"
            >
              <label
                class="font-medium text-surface-900"
                for="password_confirm"
              >
                {{ $t('login.password_confirm') }}
              </label>
              <Password
                v-model="password_confirm"
                class="w-full"
                :feedback="false"
                :fluid="true"
                :inputProps="{ autocomplete: action === ACTION_LOGIN ? 'current-password' : 'new-password' }"
                :invalid="is_password_confirm_invalid"
                :placeholder="$t('login.password_confirm')"
                required
                toggleMask
                @blur="password_confirm_touched = true"
              />
              <Message
                v-if="is_password_confirm_invalid"
                severity="error"
                size="small"
                variant="simple"
              >
                {{ $t('login.passwordConfirmRequired') }}
              </Message>
              <Message
                v-if="is_password_confirm_match_invalid"
                severity="error"
                size="small"
                variant="simple"
              >
                {{ $t('login.passwordConfirmMatchRequired') }}
              </Message>
            </div>
            <a
              v-if="action === ACTION_LOGIN"
              class="text-primary -mt-2 -mb-2 w-full cursor-pointer text-right text-sm text-gray-500 hover:text-gray-600"
              @click="toggleForgotPassword"
              >{{ $t('login.passwordForgot') }}</a
            >
            <div v-if="action === ACTION_SIGNUP">
              <Checkbox
                v-model="accept_tos"
                binary
                :invalid="is_accept_tos_invalid"
                variant="filled"
                @input="accept_tos_touched = true"
              />
              <label for="accept_tos"
                ><span @click="accept_tos = !accept_tos"> &nbsp;&nbsp;{{ $t('login.acceptTosPrefix') }} </span
                ><u
                  ><a
                    :href="TERMS_AND_CONDITIONS_URL"
                    target="_blank"
                    >{{ $t('login.acceptTos') }}</a
                  ></u
                ></label
              >
              <Message
                v-if="is_accept_tos_invalid"
                severity="error"
                size="small"
                variant="simple"
              >
                {{ $t('login.acceptTosRequired') }}
              </Message>
            </div>
            <div
              v-if="action"
              class="flex flex-wrap items-center gap-6 gap-y-4"
            >
              <Button
                class="w-full px-12!"
                :label="$t(`login.submit_${action}`)"
                :loading="loading"
                type="submit"
              />
              <Button
                v-if="google_sso"
                class="mb-2 min-w-fit flex-1 shrink-0 whitespace-nowrap"
                icon="pi pi-google !text-base !leading-none"
                label="Sign in with Google"
                :loading="loading"
                outlined
                severity="secondary"
                type="button"
                @click="loginWithGoogle"
              />
              <a
                v-if="$_.includes([ACTION_FORGOT, ACTION_RESET, ACTION_EXPIRED_RESET], action)"
                class="text-primary w-full cursor-pointer text-right text-sm text-gray-500 hover:text-gray-600"
                @click="toggleLogin"
              >
                {{ $t('login.backToLogin') }}
              </a>
            </div>
          </div>
        </form>
      </div>
      <div class="mb-1 text-center text-xs text-gray-500">
        <a
          href="https://ziggu.io"
          target="_blank"
        >
          <div class="mb-2 flex items-center justify-center">
            <img
              alt="Ziggu Logo"
              class="mt-1 h-3.75 text-gray-500"
              src="https://storage.googleapis.com/ziggu-engage-public/login/Ziggu-Logo.png"
            />
          </div>
        </a>
        <a
          class="text-gray-500 hover:text-gray-600"
          :href="COOKIE_POLICY_URL"
          target="_blank"
          >{{ $t('login.cookiePolicy') }}</a
        >
        -
        <a
          class="text-gray-500 hover:text-gray-600"
          :href="PRIVACY_POLICY_URL"
          target="_blank"
          >{{ $t('login.privacyPolicy') }}</a
        >
        -
        <a
          class="text-gray-500 hover:text-gray-600"
          :href="TERMS_AND_CONDITIONS_URL"
          target="_blank"
          >{{ $t('login.termsandconditionsPolicy') }}</a
        >
        -
        <a
          class="whitespace-nowrap text-gray-500 hover:text-gray-600"
          :href="status_page_url"
          target="_blank"
        >
          <span class="inline-flex items-center">
            Status
            <img
              alt="status page badge"
              class="ml-1 h-4 align-middle"
              src="https://ohdear.app/badges/status-page/01hn3vvv627vwdvpgfchttfe1m/small"
            />
          </span>
        </a>
      </div>
    </div>
    <div
      class="hidden w-7/12 md:block"
      :style="{
        height: '100vh',
        backgroundImage: `url('${picture_login_url}')`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundBlendMode: 'overlay, normal',
        boxShadow: '-8px 0 16px -8px rgba(0,0,0,0.08)',
        padding: '0px',
        objectFit: 'cover',
      }"
    />
  </div>
  <div
    v-else
    class="flex min-h-screen w-full items-center justify-center"
  >
    <AppLoader />
  </div>
</template>

<script setup>
import AppLoader from '@/app/shared_components/AppLoader.vue';
/* oxlint-disable max-lines */
import {
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  inMemoryPersistence,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { computed, inject, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { mapActions } from '@/store/lib/vue3helpers.js';
import Checkbox from 'primevue/checkbox';
import Button from 'primevue/button';
import { Password } from 'primevue';
import { ZIGGU_PARENT_URL, zigguSubdomainURL } from '@/app/util/urls';
import tinycolor from 'tinycolor2';
import { updatePreset } from '@primeuix/themes';
import * as Sentry from '@sentry/vue';
import INJECT_KEYS from '@/app/data/inject_keys';

const ACTION_LOGIN = 'login';
const ACTION_FORGOT = 'password_forgot';
const ACTION_RESET = 'password_reset';
const ACTION_EXPIRED_RESET = 'expired_reset';
const ACTION_EXPIRED_SIGNUP = 'expired_signup';
const ACTION_SIGNUP = 'signup';

const MESSAGE_DELAY_MS = 3000;

const COOKIE_POLICY_URL = 'https://ziggu.io/cookie-policy';
const PRIVACY_POLICY_URL = 'https://ziggu.io/privacy-policy';
const TERMS_AND_CONDITIONS_URL = 'https://ziggu.io/terms-and-conditions';

const route = useRoute();
const { t, locale } = useI18n();
const emitter = inject(INJECT_KEYS.EMITTER);

const { FETCH_USER_PROFILE, FETCH_TENANT, FETCH_AUTH_USERS, LINK_USER, LOGIN_USER, LOGOUT_USER, PASSWORD_RESET } =
  mapActions([
    'FETCH_USER_PROFILE',
    'FETCH_TENANT',
    'FETCH_AUTH_USERS',
    'LINK_USER',
    'LOGIN_USER',
    'LOGOUT_USER',
    'PASSWORD_RESET',
  ]);

const accept_tos = ref(false);
const accept_tos_touched = ref(false);
const action = ref(route.query.action || 'login');
const auth = ref(null);
const email = ref(new URLSearchParams(window.location.search).get('email') || '');
const email_touched = ref(false);
const initialized = ref(false);
const invitation_code = ref(route.query.invitation_code);
const loading = ref(false);
const message_error = ref(null);
const message_info = ref(null);
const notification = ref(route.query.notification);
const oob_code = ref(route.query.oob_code);
const password = ref('');
const password_confirm = ref('');
const password_confirm_touched = ref(false);
const password_touched = ref(false);
const picture_login_url = ref(null);
const picture_logo_url = ref(null);
const provider_google = ref(null);
const subdomain = ref(route.query.subdomain);
const user = ref(null);

const is_email_invalid = computed(() => {
  if (!email_touched.value) return false;
  if (!email.value) return true;
  return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value);
});

const is_password_invalid = computed(() => password_touched.value && !password.value);

const is_password_invalid_length = computed(() => {
  if (!_.includes([ACTION_RESET, ACTION_SIGNUP], action.value)) return false;

  return password_touched.value && !is_password_invalid.value && password.value.length < 8;
});

const is_password_confirm_invalid = computed(() => password_confirm_touched.value && !password_confirm.value);

const is_accept_tos_invalid = computed(() => accept_tos_touched.value && !accept_tos.value);

const is_password_confirm_match_invalid = computed(() => {
  if (!_.includes([ACTION_RESET, ACTION_SIGNUP], action.value)) return false;

  return (
    password_confirm_touched.value && !is_password_confirm_invalid.value && password.value !== password_confirm.value
  );
});

const is_password_reset_invalid = computed(
  () =>
    is_password_invalid.value ||
    is_password_invalid_length.value ||
    is_password_confirm_invalid.value ||
    is_password_confirm_match_invalid.value,
);

const is_signup_invalid = computed(() => is_password_reset_invalid.value || is_accept_tos_invalid.value);

const root_url = computed(() => (subdomain.value ? zigguSubdomainURL(subdomain.value) : ZIGGU_PARENT_URL));

const status_page_url = computed(() => `https://status.ziggu.app/?secret=&locale=${locale.value}`);

const google_sso = computed(
  () =>
    action.value === ACTION_LOGIN &&
    (subdomain.value === 'supervisor' ||
      (!is_email_invalid.value && email.value.endsWith('@ziggu.io') && !email.value.includes('+'))),
);

function redirect() {
  const url_params = new URLSearchParams(window.location.search);
  const redirect_path = url_params.get('redirect');

  window.location.href = root_url.value + (redirect_path || '');
}

async function createSession() {
  const id_token = user.value.accessToken;
  await LOGIN_USER({ id_token });
}

async function loginWithPassword() {
  try {
    const user_credential = await signInWithEmailAndPassword(auth.value, email.value, password.value);
    user.value = user_credential.user;
    await createSession();
    redirect();
  } catch (error) {
    switch (error.code) {
      case 'auth/invalid-credential':
      case 'auth/invalid-email':
      case 'auth/too-many-requests':
      case 'auth/network-request-failed': {
        message_error.value = t(`login.${error.code}`);
        break;
      }
      default: {
        message_error.value = `ERROR ${error.code}: ${error.message}`;
        Sentry.captureException(error);
      }
    }
    loading.value = false;
  }
}

async function loginWithGoogle() {
  if (loading.value) return;
  loading.value = true;

  try {
    const user_credential = await signInWithPopup(auth.value, provider_google.value);
    user.value = user_credential.user;
    await createSession();
    redirect();
  } catch (error) {
    if (error.code === 'auth/popup-blocked') {
      const user_credential = await signInWithRedirect(auth.value, provider_google.value);
      user.value = user_credential.user;
      await createSession();
      return;
    }

    message_error.value = `ERROR ${error.code}: ${error.message}`;
    loading.value = false;
  }
}

function resetValidations() {
  password.value = null;
  password_touched.value = false;
  email_touched.value = false;
  loading.value = false;
}

function toggleLogin() {
  resetValidations();
  message_error.value = null;
  message_info.value = null;
  action.value = ACTION_LOGIN;
}

function toggleForgotPassword() {
  resetValidations();
  message_error.value = null;
  message_info.value = null;
  action.value = ACTION_FORGOT;
}

async function signUp() {
  if (!accept_tos.value) accept_tos_touched.value = true;
  if (!password_confirm.value) password_confirm_touched.value = true;

  if (is_signup_invalid.value) return;

  loading.value = true;

  try {
    const user_credential = await createUserWithEmailAndPassword(auth.value, email.value, password.value);
    user.value = user_credential.user;
    const id_token = user.value.accessToken;
    const response = await LINK_USER({ invitation_code: invitation_code.value, id_token });
    if (response.success) {
      action.value = null;
      message_info.value = t(`login.${response.message}`);
      await createSession();
      await new Promise((resolve) => {
        setTimeout(resolve, MESSAGE_DELAY_MS);
      });
      redirect();
    } else {
      message_error.value = response.message;
      loading.value = false;
    }
  } catch (error) {
    Sentry.captureException(error);
    // Handle errors, e.g. auth/email-already-in-use, invalid-email, operation-not-allowed, weak-password
    message_error.value = `ERROR ${error.code}: ${error.message}`;
    loading.value = false;
  }
}

async function requestPasswordReset() {
  loading.value = true;
  try {
    await PASSWORD_RESET({
      subdomain: subdomain.value,
      email_address: email.value,
    });
    message_info.value = t(`login.success_${action.value}`, { email: email.value });
  } catch {
    message_error.value = 'An error occurred. Please try again later or contact us.';
  } finally {
    loading.value = false;
  }
}

async function resetPassword() {
  if (!password_confirm.value) password_confirm_touched.value = true;

  if (is_password_reset_invalid.value) return;

  loading.value = true;

  try {
    await confirmPasswordReset(auth.value, oob_code.value, password.value);
    message_info.value = t(`login.success_${action.value}`, { email: email.value });
    action.value = null;
    await new Promise((resolve) => {
      setTimeout(resolve, MESSAGE_DELAY_MS);
    });
    await loginWithPassword();
  } catch (error) {
    Sentry.captureException(error);
    message_error.value = `Error resetting password: ${error.message}`;
    // Handle errors (e.g., weak password, expired code if not pre-verified)
    // Common error codes:
    // - 'auth/expired-action-code': The OOB code has expired.
    // - 'auth/invalid-action-code': The OOB code is invalid. This can happen if the code is malformed or has already been used.
    // - 'auth/user-disabled': The user corresponding to the OOB code has been disabled.
    // - 'auth/user-not-found': There is no user corresponding to the OOB code. This may happen if the user has been deleted.
    // - 'auth/weak-password': The new password is not strong enough.
    loading.value = false;
  }
}

async function submit() {
  if (loading.value) return;

  if (!password.value) password_touched.value = true;
  if (!email.value) email_touched.value = true;

  switch (action.value) {
    case ACTION_LOGIN: {
      if (!password.value || !email.value || is_email_invalid.value) return;

      loading.value = true;
      loginWithPassword();
      break;
    }
    case ACTION_SIGNUP: {
      await signUp();
      break;
    }
    case ACTION_FORGOT: {
      if (!email.value || is_email_invalid.value) return;

      await requestPasswordReset();
      break;
    }
    case ACTION_RESET: {
      await resetPassword();
      break;
    }
    case ACTION_EXPIRED_RESET: {
      toggleForgotPassword();
      break;
    }
    case ACTION_EXPIRED_SIGNUP: {
      toggleLogin();
      break;
    }
    default: {
      break;
    }
  }
}

function initTheme(primary_color) {
  function getShade(hex, percent) {
    const color = tinycolor(hex);
    return percent >= 0 ? color.lighten(percent).toHexString() : color.darken(Math.abs(percent)).toHexString();
  }

  updatePreset({
    semantic: {
      primary: {
        50: getShade(primary_color, 45),
        100: getShade(primary_color, 35),
        200: getShade(primary_color, 25),
        300: getShade(primary_color, 15),
        400: getShade(primary_color, 7),
        500: primary_color,
        600: getShade(primary_color, -7),
        700: getShade(primary_color, -15),
        800: getShade(primary_color, -25),
        900: getShade(primary_color, -35),
        950: getShade(primary_color, -45),
      },
    },
  });
}

function initTitle(tenant_name) {
  document.title = `${tenant_name} Login`;
}

async function initialize() {
  try {
    // detect whether we're authenticated based on successful response: redirect to root
    // exception: password reset must always render the login page (to allow resetting from another device/session)
    const is_password_reset = route.query.action === ACTION_RESET && route.query.oob_code;
    const current_user = await FETCH_USER_PROFILE();
    if (is_password_reset) {
      // if authenticated as a different user, log out first before rendering the reset form
      const email_reset = (route.query.email || '').toLowerCase();
      const email_authenticated = (current_user.email_address || '').toLowerCase();
      if (email_authenticated && email_authenticated !== email_reset) {
        await LOGOUT_USER();
      }
      // fall through to render the reset form
    } else {
      redirect();
      return;
    }
  } catch (error) {
    // ignore 401 unauthorized error: proceed with login render
    if (!(error.response && error.response.status === 401)) {
      throw error;
    }
  }

  if (notification.value) {
    message_info.value = t(`login.notification_${notification.value}`);
  }

  // firebase init
  auth.value = getAuth();
  await setPersistence(auth.value, inMemoryPersistence); // long-lived session cookies avoid client side persistence
  provider_google.value = new GoogleAuthProvider();

  // fetch tenant configuration to style (and i18n) login page appropriately
  const config = await FETCH_TENANT({ subdomain: subdomain.value });
  picture_login_url.value = config.picture_login_url;
  picture_logo_url.value = config.picture_logo_url;
  locale.value = config.language;
  emitter.$emit('initFavicon', config.picture_favicon_url);
  initTitle(config.name);
  initTheme(config.theme_primary_color);

  if (action.value === ACTION_RESET && oob_code.value) {
    try {
      await verifyPasswordResetCode(auth.value, oob_code.value);
    } catch (error) {
      switch (error.code) {
        case 'auth/invalid-action-code':
        case 'auth/expired-action-code': {
          action.value = ACTION_EXPIRED_RESET;
          message_info.value = t(`login.auth/invalid-action-code`);
          break;
        }
        default: {
          Sentry.captureException(error);
          // with email enumeration protection on it this _should_ only be able to return prior error
          message_error.value = `ERROR ${error.code}: ${error.message}`;
        }
      }
    }
  } else if (action.value === ACTION_SIGNUP) {
    try {
      const invitation_code_signup = `${invitation_code.value}`;
      await FETCH_AUTH_USERS({
        email_address: email.value,
        invitation_code: invitation_code_signup,
      });
    } catch (error) {
      const { error_code } = error.response.data;
      switch (error_code) {
        case 'auth-ziggu/invalid-invitation-code': {
          action.value = ACTION_EXPIRED_SIGNUP;
          message_info.value = t(`login.${error_code}`);
          break;
        }
        default: {
          message_error.value = `${error_code}: ${error.message}`;
        }
      }
    }
  }

  initialized.value = true;
}

initialize();
</script>
